/* THE SEARCH — ONE HAYSTACK FOR THE VAULT, THE PROFILE AND RESOURCES.
 * Step 6, stop C (his build plan of 2026-09-27, C3), built 2026-10-03.
 *
 * ── ⚠⚠ WHY THIS IS A MODULE AND NOT THREE LINES IN THREE VIEWS ──────
 * The vault (`views-vault.js`), the public profile (`views-community.js`)
 * and the Resources picker each built their own search string, and the
 * register's 2026-09-20 audit found all three missing the fields the
 * recovery added: Fr 466 carries `series 1882 · backType Brown Back ·
 * charterPeriod Second` and its whole `desc` is `Bruce / Gilfillan`, so a
 * collector who read "Series 1882 · Brown Back" on the hero and typed
 * `1882` got nothing. Fr 832A's `1914` was invisible the same way, and
 * its signatures (`Burke / McAdoo`, the `signatures` field) too. ⚠ And
 * the vault's year came from `noteYear` (desc only) while its SORT read
 * `yearOf` — search and sort disagreed about what year a note had.
 * Three copies of a predicate is how one of them drifts; this is the one.
 *
 * ── WHAT THE HAYSTACK CARRIES ───────────────────────────────────────
 * Everything the three views carried before (the ref, denomination,
 * section and its one display name, `desc`, the year, the grade) PLUS the
 * recovered and amended fields — `series`, the charter period as the hero
 * prints it (*Second Charter Period*), `backType`, `signatures`, the seal
 * (*Blue Seal*, once the repair lands), the serial colour (*Red serial*),
 * the series-date position, the issue, the design and the back colour —
 * the section's own search words (his answer 9, 2026-09-27: *"the same
 * WWII words in the Resources search"*), and the detail line itself, so
 * what the hero says a note is, the search finds. The year is `yearOf`,
 * the same rule the sort and the card use (his ruling 2026-09-20).
 *
 * ── ⚠⚠ THE EXACT-PLACE RULE, HIS ────────────────────────────────────
 * His answer 7 (2026-09-27): *"Typing a state matches that state
 * exactly."* — and his 5: *"New York: exact state OR exact Federal
 * Reserve Bank name."* Measured before the rule: `Kansas` found 516 rows
 * (25 Kansas, 25 Arkansas, 465 Kansas City), `New York` 865. So a query
 * that IS a state or colony the catalogue carries (34 of them, the first
 * part of a Colonial or Obsolete row), or one of the twelve Federal
 * Reserve Banks, matches the notes carrying that state or that bank and
 * no substring: Kansas 25, New York 842 (267 + 575), Michigan 25. A word
 * the catalogue carries on no row as a place (*Hawaii*) is an ordinary
 * substring search, which is how it finds the WWII rows once they carry
 * their issue. ⚠ A custom note that carries a state (stop B's no-Fr fill
 * writes it to `desc` under *Obsolete Note*) is found by that state the
 * same way.
 */

import { FRB_DISTRICTS, rowAxes } from './note-narrow.js'
import { detailLine, yearOf } from './note-filters.js'

/** The words a section answers to beyond its name. ⚠ His answer 9: the
 *  WWII words, on the vault search AND the Resources search. */
export const SECTION_WORDS = Object.freeze({ 'WWII Emergency Note': 'WWII WW2 World War II' })

/** How a field reads in the haystack — the wording the hero and the card
 *  use, so the collector can type what they read. */
export const SEARCH_TEXT = Object.freeze({
  seal: (s) => `${s} Seal`,
  serial: (s) => `${s} serial`,
  back: (b) => `${b} back`,
  charter: (c) => `${c} Charter Period`,
})

const isCol = (n) => n.section === 'Colonial'
const isObs = (n) => n.section === 'Obsolete Note'
const present = (x) => x !== undefined && x !== null && x !== '' && x !== false

/** A query or a place, normalised once: trimmed, lower-case, one space. */
export const normQuery = (q) => String(q ?? '').trim().toLowerCase().replace(/\s+/g, ' ')

/**
 * The text a note is searched by. `ref` is what the vault shows for the
 * Fr (a collector's own reference on an Obsolete note), `year` the
 * collector's own year (`yearOf` keeps it first), `grade` the grade label,
 * `labelOf` the type's one display name (`typeLabel`).
 */
export function searchHay(n, { ref, year = '', grade = '', labelOf = (s) => s } = {}) {
  const r = ref === undefined ? n.fr : ref
  return [
    (isCol(n) ? 'colonial ' : isObs(n) ? 'obsolete ' : 'fr. ') + (r || ''),
    n.denom, n.fd, n.section, labelOf(n.section), n.desc, yearOf(year, n), grade,
    SECTION_WORDS[n.section],
    detailLine(n), n.series, n.charterPeriod && SEARCH_TEXT.charter(n.charterPeriod), n.backType, n.signatures,
    n.seal && SEARCH_TEXT.seal(n.seal), n.serial && SEARCH_TEXT.serial(n.serial), n.datePosition, n.issue, n.design, n.back && SEARCH_TEXT.back(n.back),
  ].filter(present).join(' ').toLowerCase()
}

/** The state or colony a row carries — the first part of a Colonial or
 *  Obsolete row's text, and nothing on any other section (a National Bank
 *  Note prints its bank's state in `desc`, but that is not the note's
 *  state the way a colony's is). Normalised like a query. */
export const stateOf = (n) => (isCol(n) || isObs(n) ? normQuery(String(n.desc || '').split(' · ')[0]) : '')
/** The Federal Reserve Bank a row carries, by the card's own rule (his E:
 *  the city at the start of the text, Federal Reserve sections only). */
export const bankOf = (n) => normQuery(rowAxes(n).district)

let placeCache = null
/** Every query the exact rule governs: the states and colonies the
 *  catalogue carries on a row, and the twelve Federal Reserve Banks.
 *  Computed once per catalogue. */
export function exactPlaces(catalog) {
  if (placeCache && placeCache.of === catalog) return placeCache.set
  const set = new Set(FRB_DISTRICTS.map(normQuery))
  for (const r of catalog) { const s = stateOf(r); if (s) set.add(s) }
  placeCache = { of: catalog, set }
  return set
}

/**
 * Does the note match the query? The exact-place rule when the query is
 * a place the catalogue carries; a substring of the haystack otherwise.
 * An empty query matches everything.
 */
export function searchMatch(q, n, extra = {}, catalog = null) {
  const k = normQuery(q)
  if (!k) return true
  if (catalog && exactPlaces(catalog).has(k)) return stateOf(n) === k || bankOf(n) === k
  return searchHay(n, extra).includes(k)
}
